import React, { Component } from "react";
import { CONFIG } from "../../helper/config";
import { connect } from "react-redux";
import "./home.css";
import * as microsoftTeams from '@microsoft/teams-js';
import { getHomeVas, getVADetails, updateAgentStatus } from "../../actions/notification.actions";
import history from "../../helper/history";
import { Segment, Button, Grid, GridRow, GridColumn, Icon, Loader, Dimmer } from "semantic-ui-react";
import { activeLoader, storeSessionId } from "../../actions/view.actions";

interface IProps {
  is_license_expire: boolean;
  home_va_data: any;
  va_details: any;
  storeSessionId: (arg0: string) => void;
  getHomeVas: (arg0: any, arg1: any) => void;
  getVADetails: (arg0: any, arg1: string) => void;
  updateAgentStatus: (arg0: any, arg1: string, arg2: any) => void;
}

interface IState {
  token: string;
  selected_va_index: number;
  selected_dataset_index: number;
  teams_context: any;
}

/**
 * The `Home` component is the main entry point for displaying available
 * Virtual Assistants (VAs) and their related datasets in Microsoft Teams.
 * 
 * Responsibilities:
 * - Initializes Microsoft Teams SDK and retrieves context (user, tenant, channel, etc.).
 * - Fetches and displays available Virtual Assistants (`getHomeVas`).
 * - Allows the user to select a VA and view its sample prompts and datasets (`getVADetails`).
 * - Handles both scenarios:
 *   - When context and token are provided via query parameters (WebApp case).
 *   - When context and token are retrieved from Teams SDK (Teams app case).
 * - Manages license expiration and no-access states with proper fallback UI.
 * 
 * State:
 * - `token`: Authentication token used for API requests.
 * - `selected_va_index`: Index of the currently selected Virtual Assistant.
 * - `selected_dataset_index`: Index of the currently selected dataset under a VA.
 * - `teams_context`: Microsoft Teams context object (user, tenant, app, channel info).
 * 
 * Props:
 * - `is_license_expire`: Boolean indicating if the license has expired.
 * - `home_va_data`: Data containing available Virtual Assistants and prompts.
 * - `va_details`: Detailed dataset information for the selected VA.
 * - `storeSessionId`: Action to store a generated session ID in Redux.
 * - `getHomeVas`: Action to fetch Virtual Assistants.
 * - `getVADetails`: Action to fetch dataset details for a VA.
 * 
 * Example Usage:
 * ```tsx
 * <Home
 *   is_license_expire={false}
 *   home_va_data={data}
 *   va_details={details}
 *   storeSessionId={storeSessionId}
 *   getHomeVas={getHomeVas}
 *   getVADetails={getVADetails}
 * />
 * ```
 */
export class Home extends Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);
    this.state = {
      token: '',
      selected_va_index: 0,
      teams_context: {},
      selected_dataset_index: 0
    };
  }

  /**
   * This function is called after the component has been mounted.
   * It gets the context, auth token and port number from the query parameters.
   * If the context is present, it calls the getHomeVas action with the context and token.
   * If not, it gets the context and auth token from the Microsoft Teams context,
   * and calls the getHomeVas action with the context and token.
   */
  componentDidMount() {
    const query_params = new URLSearchParams(history.location.search);
    let teams_context: any = query_params.get('context')?.split(CONFIG.URLQUERYPARAMSEPERATOR)[0];
    teams_context = teams_context ? JSON.parse(teams_context) : {};
    const port_no: any = query_params.get('port_no');
    let token = query_params.get('auth_token');
    microsoftTeams.app.initialize();
    microsoftTeams.app.notifySuccess();
    console.log("token :: ", history.location.search, query_params, token, port_no, teams_context)
    if (teams_context.userObjectId && token) {
      let info = {
        platform: "teams",
        port_no: port_no,
        user_id: teams_context.userObjectId,
        user_name: teams_context.userPrincipalName,
        tenant_id: teams_context.tid,
        appId: teams_context.appId,
        team_id: (teams_context.groupId) ? teams_context.groupId : '',
        destination_id: this.getDestinationID(teams_context),
        channelId: 'msteams',
        teams_channel_id: teams_context.channelId,
        entityId: teams_context.entityId.split("_")[0],
        group_id: teams_context.chatId ? teams_context.chatId : undefined
      }
      this.props.getHomeVas(info, token)
      this.setState({
        token: token,
        teams_context: info
      })
    } else {
      let team_context: any = {};
      microsoftTeams.app.getContext().then((context) => {
        let entity_id = context.page?.id.split("_")[0]
        team_context.user_id = context.user?.id;
        team_context.user_name = context.user?.userPrincipalName;
        team_context.tenant_id = context.user?.tenant?.id;
        team_context.appId = context.app?.appId;
        team_context.teams_channel_id = context.channel?.id;
        team_context.channelId = 'msteams';
        team_context.entityId = entity_id

        microsoftTeams.authentication.getAuthToken().then((token: string) => {
          let info = {
            platform: "teams",
            port_no: port_no, ...team_context
          };
          let sessionId = new Date().toString();
          this.props.storeSessionId(sessionId)
          this.props.getHomeVas(team_context, token)

          this.setState({
            token: token,
            teams_context: info
          })
        }).catch((error) => {
          console.error('error', error);
        })
      });
    }
  }

  /**
   * Determines the `destination_id` based on the Teams context.
   * 
   * - If a meeting is active, returns the chat ID.
   * - If a channel exists, returns the group ID.
   * - Otherwise, defaults to the user’s object ID.
   *
   * @param teams_context - The context object from Microsoft Teams.
   * @returns {string} The computed destination ID.
   */
  getDestinationID = (teams_context: any) => {
    let destination_id = "";
    if (teams_context.meetingId) {
      destination_id = teams_context.chatId;
    } else if (teams_context.teams_channel_id) {
      destination_id = teams_context.groupId;
    } else {
      destination_id = teams_context.userObjectId;
    }
    return destination_id;
  };

  /**
   * Fetches details of a Virtual Assistant (VA).
   *
   * - Calls `getVADetails` with the VA ID and Teams context.
   * - Updates the selected VA index in the component state.
   *
   * @param va_id - The ID of the selected VA.
   * @param va_index - The index of the selected VA in the VA list.
   */
  getVADetails = (va_id, va_index) => {
    let va_info = { "va_id": va_id, ...this.state.teams_context }
    this.props.getVADetails(va_info, this.state.token)
    this.setState({ selected_va_index: va_index, selected_dataset_index: 0 })
  }

  updateAgentStatus = (va_info, va_index) => {
    let va_update_info = {
      "va_id": va_info.va_id,
      "user_id": this.state.teams_context.user_id,
      "is_user_active": !va_info.is_user_active,
      ...this.state.teams_context
    }
    this.props.updateAgentStatus(va_update_info, this.state.token, this.state.teams_context)
  }


  checkAllSwitchesOff = (pros) => {
    if (!pros || pros.length === 0) {
      return false;
    }
    return pros.every((item) => !item.is_user_active);
  };
  /**
   * Renders the Home component based on the current state and props.
   * - Displays a license expiration message if the license has expired.
   * - Displays a welcome message and available use cases if the user has access.
   * - Shows related datasets and their details if use cases are available.
   * - Displays a message if the user does not have access to any use case.
   * - Shows a loading indicator if the data is being fetched.
   */

  render() {
    const { is_license_expire, home_va_data, va_details } = this.props;
    let { selected_va_index, selected_dataset_index, teams_context } = this.state;

    const isAllOff = this.checkAllSwitchesOff(home_va_data?.va_details);
        console.log("team context :: ", teams_context);

    console.log("home_va_data :: ", home_va_data, va_details, is_license_expire);
    console.log("props", this.props)
    const redirectionUrlId =  teams_context?.appId?.appIdAsString ?  teams_context?.appId?.appIdAsString : teams_context?.entityId;


    if (is_license_expire) {
      return (
        <Segment
          placeholder
          textAlign="center"
          size="large"
          content={CONFIG.LICENSE_EXPIRE}
        />
      );
    } else if (Object.keys(home_va_data).length > 0 && home_va_data?.va_details?.length > 0) {
      return (
        <div className="home-container">
          <div className="home-header">
            <h1>{`Hi ${home_va_data?.username}, Welcome to ${CONFIG?.APPNAME?.BEAGLEGPT}`}</h1>

            <Button className="redirect_btn" onClick={() => {
              console.log("URLS",`https://teams.microsoft.com/l/entity/${redirectionUrlId}/conversations?tenantId=${teams_context.tenant_id}`)
              console.log(JSON.stringify(teams_context))
              microsoftTeams.app.openLink(`https://teams.microsoft.com/l/entity/${redirectionUrlId}/conversations?tenantId=${teams_context.tenant_id}`)
            }}
            >
              <Icon name={'chat'} size="large" />
              Chat with {CONFIG?.APPNAME?.BEAGLEGPT}
            </Button>
          </div>
          <h3 className="container_heading use_cases">Manage your Agent</h3>
          <div className="home_text">
            Select an Agent to explore its capabilities. You can activate or deactivate it using the toggle switch in the panel below.
          </div>
          <div className="home_va_container">
            <div className={`va-btn-container ${isAllOff ? "fit-height" : ""}`}>
              <div className="agent-list-scrollable">

                <div className="container_heading agent-list-text"> Agents List</div>
                {home_va_data?.va_details?.map((va_info, va_index) => (
                  <div
                    key={va_info.va_name}
                    style={{
                      display: "flex",
                      marginBottom: "10px",
                      alignItems: "center",
                      width: "100%"
                    }}
                  >
                    <Button
                      title={va_info.va_name}
                      onClick={() => this.getVADetails(va_info.va_id, va_index)}
                      className={`${va_index === selected_va_index ? "selected_va" : ""} `}
                      style={{
                        flex: "0 0 75%",
                        maxWidth: "75%",
                        border: "1px solid lightgray",
                        background: "white"
                      }}
                      disabled={!va_info.is_user_active}
                    >
                      {va_info.va_name}
                    </Button>

                    <div
                      style={{
                        flex: "0 0 25%",
                        maxWidth: "25%",
                        display: "flex",
                        justifyContent: "center"
                      }}
                    >
                      <div className="toggle">
                        <input
                          id={`toggle-${va_index}`}
                          type="checkbox"
                          checked={va_info.is_user_active}
                          onChange={() => this.updateAgentStatus(va_info, va_index)}
                        />
                        <label htmlFor={`toggle-${va_index}`}></label>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {!isAllOff && (<div className="va_details_right_container">
              <div className="promps-container">
                <h2 className="container_heading">Sample prompts</h2>
                <ul className="prompts_points">
                  {home_va_data?.va_details[selected_va_index]?.prompts?.map((prompt, index) => <li key={prompt}>{prompt}</li>)}
                </ul>
              </div>
              {va_details.length > 0 ? (<div className="promps-container">
                <h2 className="container_heading">Related Datasets</h2>
                <div>
                  Here are the datasets available for the selected use case. Click on a dataset to see its associated metrics and dimensions.
                </div>
                <div className="va-btn-container-data dataset-btn-container">
                  {va_details.map((va, i) => (
                    <Button
                      key={va?.dataset_name}
                      title={va?.dataset_name}
                      compact={true}
                      size="medium"
                      onClick={() => this.setState({ selected_dataset_index: i })}
                      className={`fixed-width-btn ${selected_dataset_index === i ? "selected_dataset" : ""}`}
                    >
                      {va?.dataset_name}
                    </Button>
                  ))}
                </div>

                <Grid stackable columns={3} divided className="dataset-grd">
                  <GridRow stretched>
                    <GridColumn >
                      <div className="grid_heading">Metrics</div>
                      <div className="mct_btns">
                        <ul>
                          {va_details[selected_dataset_index].metric.map(metric => <li key={metric}>{metric}</li>)}
                        </ul>
                      </div>
                    </GridColumn>
                    <GridColumn>
                      <div className="grid_heading">Dimensions</div>
                      <div className="mct_btns">
                        <ul>
                          {va_details[selected_dataset_index].columns.map(column => <li key={column}>{column}</li>)}
                        </ul>
                      </div>
                    </GridColumn>
                    <GridColumn>
                      <div className="grid_heading">Time Period</div>
                      <div className="mct_btns">
                        <ul>
                          {va_details[selected_dataset_index].time_period.map(time_period => <li key={time_period}>{time_period}</li>)}
                        </ul>
                      </div>
                    </GridColumn>
                  </GridRow>
                </Grid>
              </div>
              ) : null}
            </div>)}
          </div>
        </div>
      );
    } else if (home_va_data && home_va_data?.va_details?.length === 0) {
      return (<>
        <div className="home-header">
          <h1>{`Hi ${home_va_data?.username}, Welcome to ${CONFIG.APPNAME.BEAGLEGPT}`}</h1>
          <Button className="redirect_btn" onClick={() => {
            microsoftTeams.app.openLink(`https://teams.microsoft.com/l/entity/${redirectionUrlId}/conversations?tenantId=${teams_context.tenant_id}`)
          }}
          >
            <Icon name={'chat'} size="large" />
            Chat with {CONFIG.APPNAME.BEAGLEGPT}
          </Button>
        </div>
        <div style={{ fontSize: '16px', margin: '10px' }}>Currently you don't have access to any Use-Case, please request access at <a href="mailto:beagle@decisionpoint.in">beagle@decisionpoint.in</a></div></>)
    } else {
      return (
        <Dimmer active style={{ zIndex: '9999', position: 'fixed' }}>
          <Loader inline='centered' size='huge' >{CONFIG.LOADER_TEXT}</Loader>
        </Dimmer>
      );
    }
  }
}

/**
 * Maps state to props for the Home component.
 * Retrieves the active_loader, is_license_expire, sessionId, home_va_data, and va_details from the state.
 * @param {Object} state - The state of the application.
 * @returns {Object} A props object containing the active_loader, is_license_expire, sessionId, home_va_data, and va_details.
 */
const mapStateToProps = (state: any) => {
  return {
    active_loader: state.viewState.active_loader,
    is_license_expire: state.viewState.is_license_expire,
    sessionId: state.viewState.sessionId,
    home_va_data: state.notificationState.home_va_data,
    va_details: state.notificationState.va_details,
  };
};

export default connect(mapStateToProps, { activeLoader, storeSessionId, getHomeVas, getVADetails, updateAgentStatus })(Home);
